import { compareRecordedDates } from '@/utils/recordedValues'
/**
 * Mock audit service — wraps the in-memory audit-log read helpers.
 */

import {
  getAllAuditEntries,
  getAuditEntriesForUser,
  getRecentLoginAttempts,
  getAuditStats,
  getLoginStats,
} from '@/data/mockAuditLog'
import { mockDelay } from '@/services/factory'
import type { AuditService, AuditStatsResult } from './audit.service'
import type { AuditLogEntry } from '@/types/session.types'

/**
 * Synthesize a small activity timeline for a resource (used by the customer
 * Activity tab in mock mode). The mock audit log has no resource-scoped data,
 * so we generate a plausible recent history seeded by the resource id.
 */
function buildResourceTimeline(resourceType: string, resourceId: string): AuditLogEntry[] {
  const now = Date.now()
  const hour = 60 * 60 * 1000
  const day = 24 * hour
  // `action`/`resourceType` are typed unions on AuditLogEntry; customer events
  // aren't in those unions yet, so cast the literals through `as`.
  const rt = resourceType as AuditLogEntry['resourceType']
  const seed: Array<
    Pick<AuditLogEntry, 'action' | 'performedByName'> & { ago: number; description: string }
  > = [
    {
      action: 'user_created' as AuditLogEntry['action'],
      performedByName: 'Priya Sharma',
      ago: 30 * day,
      description: 'Customer record created',
    },
    {
      action: 'user_updated' as AuditLogEntry['action'],
      performedByName: 'Priya Sharma',
      ago: 12 * day,
      description: 'Contact details updated',
    },
    {
      action: 'settings_updated' as AuditLogEntry['action'],
      performedByName: 'Rajesh Kumar',
      ago: 5 * day,
      description: 'Assigned CA changed',
    },
    {
      action: 'user_updated' as AuditLogEntry['action'],
      performedByName: 'Amit Patel',
      ago: 2 * hour,
      description: 'Document marked verified',
    },
  ]
  return seed.map((s, i) => ({
    id: `act-${resourceId}-${i}`,
    performedByName: s.performedByName,
    action: s.action,
    resourceType: rt,
    resourceId,
    details: { description: s.description },
    success: true,
    createdAt: new Date(now - s.ago).toISOString(),
  }))
}

export function createMockAuditService(): AuditService {
  return {
    async listAll(): Promise<AuditLogEntry[]> {
      await mockDelay(300)
      return getAllAuditEntries()
    },

    async listForUser(userId: string): Promise<AuditLogEntry[]> {
      await mockDelay(300)
      return getAuditEntriesForUser(userId)
    },

    async listForResource(resourceType: string, resourceId: string): Promise<AuditLogEntry[]> {
      await mockDelay(300)
      return buildResourceTimeline(resourceType, resourceId).sort((a, b) =>
        compareRecordedDates(a.createdAt, b.createdAt, 'desc')
      )
    },

    async listRecentLogins(limit: number): Promise<AuditLogEntry[]> {
      await mockDelay(200)
      return getRecentLoginAttempts(limit)
    },

    async getStats(): Promise<AuditStatsResult> {
      await mockDelay(200)
      return { stats: getAuditStats(), loginStats: getLoginStats() }
    },
  }
}
